/* Native lazy images + one-time, image-ready homepage reveals. No dependencies. */
(() => {
  'use strict';
  if (window.mrHomeRevealMounted) return;
  window.mrHomeRevealMounted = true;

  const pending = 'mr-home-reveal-pending';
  const entering = 'mr-home-reveal-enter';
  const selector = [
    '.mr-cards__heading', '.mr-cards__card', '.mr-cards__footer',
    '.mrs-header', '.mrs-viewport', '.mrs-controls', '.mr-home-atlas',
    '.hft-product-wrap', '.hft-card-wrap'
  ].join(',');
  const seen = new WeakSet();
  const waiting = new Set();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let observer;
  let failed = false;
  const canAnimate = () => !failed && !motion.matches &&
    'IntersectionObserver' in window && !window.Shopify?.designMode;

  function showImmediately(element) {
    waiting.delete(element);
    observer?.unobserve(element);
    element.classList.remove(pending, entering);
    element.style.removeProperty('--mr-home-reveal-delay');
  }

  function showAll() {
    observer?.disconnect();
    document.querySelectorAll('.' + pending + ',.' + entering).forEach(showImmediately);
  }

  function imageReady(img) {
    // A loading failure must never hide the text or controls around an image.
    return new Promise(resolve => {
      let settled = false;
      let timer;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        img.removeEventListener('load', loaded);
        img.removeEventListener('error', done);
        resolve();
      };
      const loaded = () => {
        if (typeof img.decode === 'function') img.decode().then(done, done);
        else done();
      };
      timer = setTimeout(done, 1500);
      if (img.complete) { loaded(); return; }
      img.addEventListener('load', loaded, { once: true });
      img.addEventListener('error', done, { once: true });
      // Only promote images whose reveal has reached the viewport.
      img.loading = 'eager';
    });
  }

  function reveal(element, delay) {
    observer?.unobserve(element);
    if (!waiting.has(element)) return;
    if (!canAnimate()) { showImmediately(element); return; }
    const images = Array.from(element.querySelectorAll('img')).filter(img =>
      !img.closest('[hidden]') && img.getClientRects().length &&
      (img.currentSrc || img.getAttribute('src') || img.getAttribute('srcset'))
    );
    Promise.all(images.map(imageReady)).then(() => {
      if (!waiting.has(element) || !element.isConnected) return;
      if (!canAnimate()) { showImmediately(element); return; }
      waiting.delete(element);
      element.style.setProperty('--mr-home-reveal-delay', delay + 'ms');
      element.classList.remove(pending);
      element.classList.add(entering);
      // Remove animation styles after the photo settles so hover stays independent.
      setTimeout(() => {
        element.classList.remove(entering);
        element.style.removeProperty('--mr-home-reveal-delay');
      }, 2700 + delay);
    }).catch(() => showImmediately(element));
  }

  function init(scope) {
    const main = document.querySelector('body.template-index #MainContent');
    if (!main) return;
    // Loading is declared in Liquid before parsing; decoding is enhanced here.
    // The opening hero and its slideshow stay eager and never receive reveal classes.
    main.querySelectorAll('img[loading="lazy"]').forEach(img => { img.decoding = 'async'; });
    if (!canAnimate()) return;
    try {
      if (!observer) observer = new IntersectionObserver(entries => {
        const visible = entries.filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top ||
            a.boundingClientRect.left - b.boundingClientRect.left);
        let rowTop = -Infinity;
        let parent = null;
        let step = 0;
        visible.forEach(entry => {
          const element = entry.target;
          if (element.matches('.mr-cards__card') && element.parentElement === parent &&
              Math.abs(entry.boundingClientRect.top - rowTop) < 60) step++;
          else { step = 0; rowTop = entry.boundingClientRect.top; parent = element.parentElement; }
          reveal(element, Math.min(step, 3) * 150);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -56px 0px' });

      const candidates = Array.from(scope.querySelectorAll(selector));
      if (scope.matches?.(selector)) candidates.unshift(scope);
      const measurements = candidates.filter(element => main.contains(element) && !seen.has(element))
        .map(element => ({element, rect:element.getBoundingClientRect()}));
      measurements.forEach(({element, rect}) => {
        if (!main.contains(element) || seen.has(element)) return;
        seen.add(element);
        // Preserve already-visible content and restored scroll positions.
        if (!rect.height || rect.top < window.innerHeight) return;
        waiting.add(element);
        element.classList.add(pending);
        observer.observe(element);
      });
    } catch (_) {
      failed = true;
      showAll();
    }
  }

  const boot = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    waiting.forEach(element => { if (event.target.contains(element)) showImmediately(element); });
  });
  document.addEventListener('focusin', event => {
    const element = event.target.closest?.('.' + pending + ',.' + entering);
    if (element) showImmediately(element);
  });
  // Search, keyboard navigation, printing and reduced motion always expose content.
  document.addEventListener('beforematch', showAll);
  window.addEventListener('beforeprint', showAll);
  window.addEventListener('pageshow', event => { if (event.persisted) showAll(); });
  if (motion.addEventListener) motion.addEventListener('change', () => {
    if (motion.matches) showAll();
  });
})();
